import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ListChecks, CheckCircle2, Clock, AlertTriangle, TrendingUp, TrendingDown, AlertOctagon, Bell, UserCheck, CalendarClock, FileCheck2, Palmtree, ChevronDown, ChevronUp, Megaphone, PartyPopper, HeartHandshake, Info, Zap, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useProjectFilter } from "@/lib/projectFilter";
import { STATUS_LABEL, STATUS_COLOR, DIVISION_COLOR, PRIORITY_COLOR, PRIORITY_LABEL, fmtDate, fmtDuration, formatIDR, PROJECT_STATUS } from "@/lib/constants";
import ProjectFilterChips from "@/components/ProjectFilterChips";
import BacklogRealisasiWidget from "@/components/BacklogRealisasiWidget";
import StaffCashRecapWidget from "@/components/StaffCashRecapWidget";
import RetentionClaimStatusWidget from "@/components/RetentionClaimStatusWidget";
import CollectionDashboardWidget from "@/components/CollectionDashboardWidget";
import PortfolioProfitCard from "@/components/PortfolioProfitCard";
import OverduePromiseWidget from "@/components/OverduePromiseWidget";
import DashboardSettingsDialog, { Widget } from "@/components/DashboardSettingsDialog";
import AgendaTodayWidget from "@/components/AgendaTodayWidget";
import { useDashboardConfig, DASHBOARD_VIEWS } from "@/lib/dashboardConfig";
// 2026-06-20 (user request "REKONSILIASI ANGKA KEUANGAN DASHBOARD"): SATU
// sumber angka + tooltip rumus + label periode.
import { FINRECON_TOOLTIPS, formatPeriodLabel } from "@/lib/financeReconciliation";

// Category → icon/color meta for announcements shown on the dashboard.
const CAT_META = {
  general:    { label: "Umum",       icon: Info,           border: "border-l-blue-500",    bg: "bg-blue-100",    icon_cls: "text-blue-700",    pill: "bg-blue-100 text-blue-800" },
  holiday:    { label: "Hari Libur", icon: PartyPopper,    border: "border-l-emerald-500", bg: "bg-emerald-100", icon_cls: "text-emerald-700", pill: "bg-emerald-100 text-emerald-800" },
  condolence: { label: "Duka Cita",  icon: HeartHandshake, border: "border-l-slate-500",   bg: "bg-slate-200",   icon_cls: "text-slate-700",   pill: "bg-slate-200 text-slate-800" },
  urgent:     { label: "Mendesak",   icon: Zap,            border: "border-l-rose-500",    bg: "bg-rose-100",    icon_cls: "text-rose-700",    pill: "bg-rose-100 text-rose-800" },
};

function StatCard({ label, value, icon: Icon, accent, loading, error, hint }) {
  const display = loading ? <span className="text-slate-300 italic text-base">Memuat…</span>
    : error ? <span className="text-rose-400 italic text-base">Gagal muat</span>
    : value == null ? <span className="text-slate-300 italic text-base">—</span>
    : value;
  return (
    <div className="bg-white border border-slate-200 p-5" title={hint}>
      <div className="flex items-center justify-between">
        <p className="overline text-slate-500">{label}</p>
        <Icon size={16} className={accent || "text-slate-400"} />
      </div>
      <div className="font-display text-3xl mt-2 tabular-nums tracking-tight text-[#0B1D3A]">{display}</div>
    </div>
  );
}

// 2026-06-20 (user request): Widget "Rekonsiliasi Keuangan" — 7 metrik
// TERPISAH dari SATU endpoint /api/dashboard/finance-reconciliation. Sumber
// yang sama dgn Laporan Keuangan & Kas & Bank. Tooltip berisi rumus. Label
// "Per tanggal …". Saldo awal DIPISAH dgn label eksplisit.
function FinanceReconWidget() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    setBusy(true); setErr(null);
    const p = new URLSearchParams();
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    api.get(`/dashboard/finance-reconciliation${p.toString() ? "?" + p : ""}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Tidak dapat memuat."); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [range.from, range.to]);
  const empty = !busy && data && !data.has_data;
  const F = (k, label, tone = "navy") => {
    if (!data) return null;
    const v = data[k];
    const toneCls = tone === "green" ? "text-emerald-700" : tone === "red" ? "text-rose-700" : tone === "amber" ? "text-amber-700" : tone === "sky" ? "text-sky-700" : "text-[#0B1D3A]";
    return (
      <div className="bg-white border border-slate-200 p-3 group" data-testid={`finrecon-${k}`} title={FINRECON_TOOLTIPS[k]}>
        <div className="flex items-center gap-1.5">
          <span className="overline text-[9px] text-slate-500 tracking-wider truncate">{label}</span>
          <Info size={10} className="ml-auto text-slate-300 group-hover:text-slate-500 shrink-0" />
        </div>
        <div className={`font-display text-lg tabular-nums mt-1 ${toneCls}`}>
          {empty ? <span className="text-slate-300 italic text-xs">belum tersedia</span> : formatIDR(v)}
        </div>
      </div>
    );
  };
  return (
    <section className="bg-white border border-slate-200 p-4" data-testid="finance-recon-widget">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <h2 className="text-sm font-semibold text-[#0B1D3A]">Rekonsiliasi Keuangan</h2>
          <p className="text-[10px] text-slate-500" data-testid="finrecon-period-label">
            {data ? formatPeriodLabel(data.period) : "Memuat…"} · sumber: mutasi rekening bank
          </p>
        </div>
        <div className="flex items-center gap-1 text-[10px]">
          <input type="date" data-testid="finrecon-from" value={range.from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="border border-slate-300 px-1.5 py-0.5 tabular-nums" />
          <span className="text-slate-400">→</span>
          <input type="date" data-testid="finrecon-to" value={range.to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="border border-slate-300 px-1.5 py-0.5 tabular-nums" />
          {(range.from || range.to) && (
            <button type="button" data-testid="finrecon-reset" onClick={() => setRange({ from: "", to: "" })}
              className="ml-1 px-1.5 py-0.5 border border-slate-300 uppercase tracking-wider font-semibold text-slate-600 hover:bg-slate-50">
              Reset
            </button>
          )}
        </div>
      </div>
      {err && <div className="text-[11px] text-rose-600 italic" data-testid="finrecon-err">{err}</div>}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {F("penerimaan_invoice", "Penerimaan Invoice", "green")}
          {F("pemasukan_lainnya", "Pemasukan Lainnya", "sky")}
          {F("total_mutasi_masuk", "Total Mutasi Masuk", "green")}
          {F("pengeluaran", "Pengeluaran", "red")}
          {F("saldo_bersih_periode", "Saldo Bersih Periode", data.saldo_bersih_periode < 0 ? "red" : "navy")}
          {F("invoice_belum_diterima", "Invoice Belum Diterima", "amber")}
          {F("retensi_belum_jt", "Retensi Belum Jatuh Tempo", "sky")}
        </div>
      )}
      {data && (
        <div className="mt-2 grid grid-cols-2 gap-2" data-testid="finrecon-saldo-row">
          <div className="bg-slate-50 border border-slate-200 p-2 text-[11px]" data-testid="finrecon-saldo_awal_gabungan"
               title={FINRECON_TOOLTIPS.saldo_awal_gabungan}>
            <div className="overline text-[9px] text-slate-500">Saldo Awal Rekening <span className="italic">(di luar mutasi periode)</span></div>
            <div className="tabular-nums font-semibold text-slate-700 mt-0.5">{formatIDR(data.saldo_awal_gabungan)}</div>
          </div>
          <div className="bg-slate-50 border border-slate-200 p-2 text-[11px]" data-testid="finrecon-saldo_akhir_gabungan"
               title={FINRECON_TOOLTIPS.saldo_akhir_gabungan}>
            <div className="overline text-[9px] text-slate-500">Saldo Akhir Rekening (setelah mutasi periode)</div>
            <div className="tabular-nums font-semibold text-[#0B1D3A] mt-0.5">{formatIDR(data.saldo_akhir_gabungan)}</div>
          </div>
        </div>
      )}
      {data && !data.reconciles && (
        <div className="mt-2 text-[10px] text-rose-600 font-semibold" data-testid="finrecon-warn">
          ⚠ Rekonsiliasi tidak seimbang — hubungi admin.
        </div>
      )}
    </section>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const { selectedIds } = useProjectFilter();
  const nav = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [tasksErr, setTasksErr] = useState(false);
  const [perf, setPerf] = useState(null);
  const [projects, setProjects] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  const canSeeFinancials = canPerm(user, "projects", "view");
  const canReviewLeaveFat = canPerm(user, "leaves", "update");
  const canReviewLeaveHr = canPerm(user, "leaves", "update");
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 149-cl: Dashboard config (per-view: Daily/Weekly/Monthly) + backend sync
  const { activeView, setActiveView } = useDashboardConfig({ user, syncBackend: true });
  // 149-cw-r120 (2026-08-29): "Update Accurate Terakhir" info dihapus — Accurate
  // Update Center tidak lagi jadi antarmuka user-facing. State ditinggal agar
  // widget lama tetap render null tanpa crash.
  const [accurateLastUpdate] = useState(null);

  const reloadPerf = useCallback(() => {
    setTasksErr(false);
    api.get("/tasks").then(({ data }) => { setTasks(data || []); setTasksLoaded(true); })
      .catch(() => { setTasksErr(true); setTasksLoaded(true); });
    api.get("/performance").then(({ data }) => setPerf(data)).catch(() => {});
    if (canSeeFinancials) {
      api.get("/active-projects").then(({ data }) => setProjects(data)).catch(() => {});
    }
    api.get("/leave-requests").then(({ data }) => setLeaveRequests(data || [])).catch(() => setLeaveRequests([]));
    api.get("/announcements/active").then(({ data }) => setAnnouncements(data || [])).catch(() => setAnnouncements([]));
  }, [canSeeFinancials]);

  // Initial fetch
  useEffect(() => { reloadPerf(); }, [reloadPerf]);

  // Global refresh event — dispatched by the header refresh button — refetches
  // all data without dropping the current dashboard state (scroll position, etc.).
  useEffect(() => {
    const h = () => reloadPerf();
    window.addEventListener("ens:refresh", h);
    return () => window.removeEventListener("ens:refresh", h);
    /* eslint-disable-next-line */
  }, [canSeeFinancials]);

  // Re-fetch when filter goes from non-empty → empty (Reset to All).
  const prevIdsCount = useRef(selectedIds.length);
  useEffect(() => {
    if (prevIdsCount.current > 0 && selectedIds.length === 0) {
      reloadPerf();
    }
    prevIdsCount.current = selectedIds.length;
    /* eslint-disable-next-line */
  }, [selectedIds]);

  // Trend comparison timeframe for the filter-summary section
  const [trendPeriod, setTrendPeriod] = useState("month"); // "month" | "quarter" | "ytd" | "custom"
  // Collapsible sections — persist state per browser via localStorage.
  const [collapseYear, setCollapseYear] = useState(() => localStorage.getItem("ens_dash_collapse_year") === "1");
  const [collapseMonth, setCollapseMonth] = useState(() => localStorage.getItem("ens_dash_collapse_month") === "1");
  const toggleYear = () => setCollapseYear((v) => { const nv = !v; localStorage.setItem("ens_dash_collapse_year", nv ? "1" : "0"); return nv; });
  const toggleMonth = () => setCollapseMonth((v) => { const nv = !v; localStorage.setItem("ens_dash_collapse_month", nv ? "1" : "0"); return nv; });
  const today = new Date();
  const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const [customRange, setCustomRange] = useState(() => {
    const curEnd = new Date(today);
    const curStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const prevEnd = new Date(curStart); prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd.getFullYear(), prevEnd.getMonth(), 1);
    return { curStart: toIso(curStart), curEnd: toIso(curEnd), prevStart: toIso(prevStart), prevEnd: toIso(prevEnd) };
  });

  const filteredTasks = selectedIds.length > 0
    ? tasks.filter((t) => selectedIds.includes(t.project_id))
    : tasks;

  // 2026-10-13 (R164 — konsistensi kartu/widget tugas):
  //   Scope eksplisit — status 'dibatalkan' TIDAK termasuk aktif.
  const ACTIVE_STATUS = (s) => s !== "selesai" && s !== "dibatalkan";
  const forMe = filteredTasks.filter((t) => t.assignee_id === user.id && ACTIVE_STATUS(t.status));
  const byMe = filteredTasks.filter((t) => t.creator_id === user.id);
  const byMeActive = byMe.filter((t) => ACTIVE_STATUS(t.status) && t.assignee_id !== user.id);  // beda scope: tugas yg saya buat utk orang lain, msh aktif
  const awaitingMyVerification = byMe.filter((t) => t.status === "menunggu_verifikasi");

  // ── Pending Actions (leave / delegation) — visible to the current user
  const pendingDelegateForMe = leaveRequests.filter((lr) => lr.delegate_id === user.id && lr.status === "pending_delegate");
  const myPendingLeaves = leaveRequests.filter((lr) => lr.user_id === user.id && ["pending", "pending_delegate", "pending_fat", "pending_hr"].includes(lr.status));
  const leavesAwaitingFat = canReviewLeaveFat ? leaveRequests.filter((lr) => lr.status === "pending_fat" || (lr.status === "pending" && (lr.delegate_id ? lr.delegate_status === "approved" : true))) : [];
  const leavesAwaitingHr = canReviewLeaveHr ? leaveRequests.filter((lr) => lr.status === "pending_hr") : [];
  // ── On-Leave Today: karyawan yang sedang cuti hari ini + delegate-nya
  const onLeaveToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().slice(0, 10);
    return (leaveRequests || []).filter((lr) => {
      if (lr.status !== "approved") return false;
      const s = (lr.start_date || "").slice(0, 10);
      const e = (lr.end_date || lr.start_date || "").slice(0, 10);
      return s && e && s <= todayStr && todayStr <= e;
    }).sort((a, b) => (a.user_name || "").localeCompare(b.user_name || ""));
  }, [leaveRequests]);
  const totalPendingActions = forMe.length + awaitingMyVerification.length + pendingDelegateForMe.length + myPendingLeaves.length + leavesAwaitingFat.length + leavesAwaitingHr.length;

  // 2026-10-13 (R164): SATU sumber angka — `tasks` dari /api/tasks. Perf
  // dipakai HANYA utk `overdue` yg butuh perhitungan server. Jangan campur
  // dua endpoint utk kartu yg sama (dulu `total` fallback ke
  // perf.summary.total_tasks dgn scope BEDA → kartu 0 sementara daftar isi).
  // Loading & error state eksplisit — jangan menampilkan 0 saat belum dimuat.
  const _taskSource = selectedIds.length > 0 ? filteredTasks : tasks;
  const totals = {
    total: _taskSource.length,
    completed: _taskSource.filter((t) => t.status === "selesai").length,
    pending: _taskSource.filter((t) => ACTIVE_STATUS(t.status)).length,
    cancelled: _taskSource.filter((t) => t.status === "dibatalkan").length,
    overdue: perf?.summary?.total_overdue ?? null,  // null = belum tersedia (bukan 0)
  };

  return (
    <div className="p-6 flex flex-col gap-6">
      <DashboardSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      {/* ── Announcements (active for current user) — FIRST section on every staff dashboard */}
      <Widget id="announcements">
      {announcements.length > 0 && (
        <section className="bg-white border border-slate-200" data-testid="section-announcements">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-orange-50">
            <div className="flex items-center gap-2">
              <Megaphone size={16} className="text-[#FF5A1F]" />
              <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Pengumuman</h2>
              <span data-testid="announce-count" className="px-2 py-0.5 text-[10px] font-bold tabular-nums bg-[#FF5A1F] text-white">{announcements.length}</span>
            </div>
          </header>
          <div className="divide-y divide-slate-100">
            {announcements.map((a) => {
              const meta = CAT_META[a.category] || CAT_META.general;
              const Icon = meta.icon;
              // 2026-09-22 (user request): pengumuman/notifikasi bisa DIKLIK —
              // pindah ke halaman detailnya. Prioritas: field `link` dari
              // backend → fallback meta lama (escalate petty cash → Weekly
              // Petty Cash + popup item via ?open=<id>; retensi → monitoring
              // project) → default halaman Info & Agenda tab Pengumuman.
              const href = a.link
                || (a.meta_pc_escalate_id ? `/petty-cash?open=${a.meta_pc_escalate_id}` : null)
                || (a.meta_retention_project_id ? `/projects/${a.meta_retention_project_id}/monitoring` : null)
                || "/info-agenda?tab=pengumuman";
              return (
                <div key={a.id} data-testid={`announce-item-${a.id}`}
                  role="button" tabIndex={0}
                  onClick={() => nav(href)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(href); } }}
                  title="Klik untuk membuka detail"
                  className={`px-4 py-3 border-l-4 ${meta.border} cursor-pointer hover:bg-slate-50 transition-colors group`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${meta.bg}`}>
                      <Icon size={15} className={meta.icon_cls} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${meta.pill}`}>{meta.label}</span>
                        <h3 className="font-display text-sm tracking-tight text-[#0B1D3A]">{a.title}</h3>
                      </div>
                      <p className="text-xs text-slate-700 whitespace-pre-wrap mt-1 leading-relaxed">{a.body}</p>
                      <div className="text-[10px] text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap">
                        <span>Berlaku: {fmtDate(a.start_date)} – {fmtDate(a.end_date)}</span>
                        <span className="italic">· dari {a.author_name} ({a.author_division})</span>
                        {a.acknowledged && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider" data-testid={`ack-badge-${a.id}`}>
                            <CheckCircle2 size={9} /> Sudah diterima
                          </span>
                        )}
                      </div>
                    </div>
                    <ArrowRight size={14} className="shrink-0 text-slate-300 group-hover:text-[#FF5A1F] mt-1 transition-colors" data-testid={`announce-open-${a.id}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
      </Widget>

      <Widget id="greeting">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-slate-500">Dasbor · {DASHBOARD_VIEWS.find((v) => v.id === activeView)?.label || "Harian"}</p>
          <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Halo, {user.name.split(" ")[0]}.</h1>
          <p className="text-sm text-slate-500 mt-1">Ringkasan tugas dan kinerja dalam cakupan akses Anda.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* 149-cl: View selector Daily / Weekly / Monthly */}
          <div className="inline-flex border border-slate-300 rounded-none overflow-hidden" data-testid="dashboard-view-toggle">
            {DASHBOARD_VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                data-testid={`dashboard-view-${v.id}`}
                onClick={() => setActiveView(v.id)}
                className={"px-3 h-8 text-[11px] uppercase tracking-wider font-semibold transition-colors " + (activeView === v.id ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
              >
                {v.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="btn-dashboard-settings"
            onClick={() => setSettingsOpen(true)}
            title="Atur widget dashboard (urutan & tampilan) untuk view aktif"
            className="flex items-center gap-1.5 px-3 h-8 border border-slate-200 hover:border-[#FF5A1F] hover:text-[#FF5A1F] text-slate-600 text-[11px] uppercase tracking-wider font-semibold transition-colors"
          >
            <LayoutGrid size={12} /> Atur Widget
          </button>
        </div>
      </div>
      {/* 149-cl: "Update Accurate Terakhir" strip — bukan "Sinkronisasi", karena
          Accurate belum terintegrasi API. Ini hanya waktu update MANUAL terbaru. */}
      {accurateLastUpdate?.updated_at && (
        <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-2" data-testid="dashboard-accurate-last-update">
          <span className="uppercase tracking-wider font-semibold text-slate-400">Update Accurate Terakhir:</span>
          <span className="tabular-nums text-slate-700">
            {new Date(accurateLastUpdate.updated_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </span>
          {accurateLastUpdate.actor_name && <span className="text-slate-400">· oleh {accurateLastUpdate.actor_name}</span>}
        </div>
      )}
      </Widget>

      <Widget id="project_filter"><ProjectFilterChips /></Widget>
      <Widget id="finance_recon"><FinanceReconWidget /></Widget>

      {/* 149-cl: Agenda + Reminder hari ini (+ overdue) — Daily view */}
      <Widget id="agenda_today"><AgendaTodayWidget /></Widget>

      {/* Backlog Realisasi — top 5 petty cash tertunda (BOD/FAT only) */}
      <Widget id="backlog_realisasi"><BacklogRealisasiWidget user={user} /></Widget>
      <Widget id="retention_claim_status">{canSeeFinancials && <RetentionClaimStatusWidget />}</Widget>
      <Widget id="collection_widget">{canSeeFinancials && <><PortfolioProfitCard testid="dash-portfolio" /><CollectionDashboardWidget user={user} /></>}</Widget>
      <Widget id="overdue_promise">{canSeeFinancials && <OverduePromiseWidget />}</Widget>
      <Widget id="staff_cash_recap">{canSeeFinancials && <StaffCashRecapWidget />}</Widget>

      {/* ── Aksi Menunggu (Pending Actions) — task assignments, delegations & leave approvals awaiting the current user */}
      <Widget id="pending_actions">
      <section className="bg-white border border-slate-200" data-testid="section-pending-actions">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-amber-700" />
            <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Aksi Menunggu Anda</h2>
            <span data-testid="pending-total-badge" className={`px-2 py-0.5 text-[10px] font-bold tabular-nums ${totalPendingActions > 0 ? "bg-amber-600 text-white" : "bg-slate-200 text-slate-500"}`}>{totalPendingActions}</span>
          </div>
          <span className="text-[10px] text-slate-500 italic">Semua permintaan / tugas yang belum Anda kerjakan</span>
        </header>
        {totalPendingActions === 0 ? (
          <div className="p-6 text-center">
            <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
            <p className="text-sm text-slate-600 font-medium">Semuanya sudah beres. Tidak ada aksi tertunda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 divide-x divide-y md:divide-y-0 divide-slate-100">
            {/* 1. Tasks assigned to me — waiting to be worked on */}
            {forMe.length > 0 && (
              <div className="p-4" data-testid="pending-tasks-forme">
                <div className="flex items-center gap-2 mb-2">
                  <ListChecks size={14} className="text-blue-600" />
                  <span className="overline text-slate-600 font-semibold">Tugas Untuk Anda</span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-blue-100 text-blue-700 border border-blue-200">{forMe.length}</span>
                </div>
                <ul className="space-y-1">
                  {forMe.slice(0, 3).map((t) => (
                    <li key={t.id} data-testid={`pending-task-${t.id}`} className="text-xs flex items-center gap-2">
                      <span className={`px-1 py-0.5 text-[9px] border ${STATUS_COLOR[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                      <span className="flex-1 truncate text-[#0B1D3A]">{t.title}</span>
                      <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(t.deadline)}</span>
                    </li>
                  ))}
                </ul>
                {forMe.length > 3 && <button onClick={() => nav("/tasks")} className="mt-2 text-[10px] uppercase tracking-wider text-[#FF5A1F] font-semibold">+{forMe.length - 3} lainnya · lihat semua →</button>}
              </div>
            )}
            {/* 2. Tasks I created — awaiting my verification */}
            {awaitingMyVerification.length > 0 && (
              <div className="p-4" data-testid="pending-verify">
                <div className="flex items-center gap-2 mb-2">
                  <FileCheck2 size={14} className="text-purple-600" />
                  <span className="overline text-slate-600 font-semibold">Menunggu Verifikasi Anda</span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-purple-100 text-purple-700 border border-purple-200">{awaitingMyVerification.length}</span>
                </div>
                <ul className="space-y-1">
                  {awaitingMyVerification.slice(0, 3).map((t) => (
                    <li key={t.id} className="text-xs flex items-center gap-2">
                      <span className="flex-1 truncate text-[#0B1D3A]">{t.title}</span>
                      <span className="text-[10px] text-slate-500 truncate max-w-[100px]">← {t.assignee_name}</span>
                    </li>
                  ))}
                </ul>
                {awaitingMyVerification.length > 3 && <button onClick={() => nav("/tasks")} className="mt-2 text-[10px] uppercase tracking-wider text-[#FF5A1F] font-semibold">+{awaitingMyVerification.length - 3} lainnya →</button>}
              </div>
            )}
            {/* 3. Delegation requests — someone asked me to cover their tasks during their leave */}
            {pendingDelegateForMe.length > 0 && (
              <div className="p-4" data-testid="pending-delegation">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck size={14} className="text-emerald-600" />
                  <span className="overline text-slate-600 font-semibold">Permintaan Delegasi Tugas</span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-emerald-100 text-emerald-700 border border-emerald-200">{pendingDelegateForMe.length}</span>
                </div>
                <ul className="space-y-1">
                  {pendingDelegateForMe.slice(0, 3).map((lr) => (
                    <li key={lr.id} data-testid={`pending-delegate-${lr.id}`} className="text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#0B1D3A] truncate">{lr.user_name}</span>
                        <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(lr.start_date)}</span>
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">Cuti: {lr.leave_type} · butuh Anda backup tugas</div>
                    </li>
                  ))}
                </ul>
                <button onClick={() => nav("/attendance?tab=cuti_izin")} className="mt-2 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Buka & respons →</button>
              </div>
            )}
            {/* 4. My own leave requests still in pipeline */}
            {myPendingLeaves.length > 0 && (
              <div className="p-4" data-testid="pending-my-leaves">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock size={14} className="text-amber-600" />
                  <span className="overline text-slate-600 font-semibold">Pengajuan Cuti Saya</span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-amber-100 text-amber-700 border border-amber-200">{myPendingLeaves.length}</span>
                </div>
                <ul className="space-y-1">
                  {myPendingLeaves.slice(0, 3).map((lr) => {
                    const label = lr.status === "pending_delegate" ? "Menunggu Delegasi"
                      : lr.status === "pending_fat" ? "Menunggu FAT"
                      : lr.status === "pending_hr" ? "Menunggu HR"
                      : "Menunggu";
                    return (
                      <li key={lr.id} className="text-xs flex items-center gap-2">
                        <span className="px-1 py-0.5 text-[9px] border border-amber-300 bg-amber-50 text-amber-800">{label}</span>
                        <span className="flex-1 truncate">{lr.leave_type}</span>
                        <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(lr.start_date)}</span>
                      </li>
                    );
                  })}
                </ul>
                <button onClick={() => nav("/attendance?tab=cuti_izin")} className="mt-2 text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Lihat status →</button>
              </div>
            )}
            {/* 5. Leaves awaiting FAT (Finance & Admin) review */}
            {leavesAwaitingFat.length > 0 && (
              <div className="p-4" data-testid="pending-fat-review">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock size={14} className="text-cyan-600" />
                  <span className="overline text-slate-600 font-semibold">Cuti Menunggu Review FAT</span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-cyan-100 text-cyan-700 border border-cyan-200">{leavesAwaitingFat.length}</span>
                </div>
                <ul className="space-y-1">
                  {leavesAwaitingFat.slice(0, 3).map((lr) => (
                    <li key={lr.id} className="text-xs flex items-center gap-2">
                      <span className="font-semibold text-[#0B1D3A] truncate flex-1">{lr.user_name}</span>
                      <span className="text-[10px] text-slate-500">{lr.leave_type}</span>
                      <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(lr.start_date)}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => nav("/attendance?tab=cuti_izin")} className="mt-2 text-[10px] uppercase tracking-wider text-cyan-700 font-semibold">Review →</button>
              </div>
            )}
            {/* 6. Leaves awaiting HR (final) review */}
            {leavesAwaitingHr.length > 0 && (
              <div className="p-4" data-testid="pending-hr-review">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarClock size={14} className="text-rose-600" />
                  <span className="overline text-slate-600 font-semibold">Cuti Menunggu Persetujuan HR</span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-rose-100 text-rose-700 border border-rose-200">{leavesAwaitingHr.length}</span>
                </div>
                <ul className="space-y-1">
                  {leavesAwaitingHr.slice(0, 3).map((lr) => (
                    <li key={lr.id} className="text-xs flex items-center gap-2">
                      <span className="font-semibold text-[#0B1D3A] truncate flex-1">{lr.user_name}</span>
                      <span className="text-[10px] text-slate-500">{lr.leave_type}</span>
                      <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(lr.start_date)}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => nav("/attendance?tab=cuti_izin")} className="mt-2 text-[10px] uppercase tracking-wider text-rose-700 font-semibold">Setujui →</button>
              </div>
            )}
          </div>
        )}
      </section>
      </Widget>

      {/* ── Karyawan Cuti Hari Ini + Delegasi Tugas */}
      <Widget id="on_leave_today">
      <section className="bg-white border border-slate-200" data-testid="section-on-leave-today">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-cyan-50">
          <div className="flex items-center gap-2">
            <Palmtree size={16} className="text-cyan-700" />
            <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Karyawan Cuti Hari Ini</h2>
            <span data-testid="on-leave-count" className={`px-2 py-0.5 text-[10px] font-bold tabular-nums ${onLeaveToday.length > 0 ? "bg-cyan-600 text-white" : "bg-slate-200 text-slate-500"}`}>{onLeaveToday.length}</span>
          </div>
          <span className="text-[10px] text-slate-500 italic">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
        </header>
        {onLeaveToday.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">
            <CheckCircle2 size={22} className="mx-auto text-emerald-500 mb-1" />
            Tidak ada karyawan yang sedang cuti hari ini.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {onLeaveToday.map((lr) => {
              const s = new Date(lr.start_date);
              const e = new Date(lr.end_date || lr.start_date);
              const daysRemaining = Math.max(0, Math.ceil((e.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
              return (
                <div key={lr.id} data-testid={`on-leave-row-${lr.id}`} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50">
                  <div className="w-9 h-9 bg-cyan-100 text-cyan-800 flex items-center justify-center font-display font-semibold text-xs shrink-0">
                    {(lr.user_name || "?").split(" ").slice(0, 2).map((n) => n[0]).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0B1D3A] truncate">{lr.user_name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[lr.user_division] || "bg-slate-100"}`}>{lr.user_division}</span>
                      <span className="uppercase tracking-wider font-semibold text-cyan-700">{lr.leave_type}</span>
                      <span className="tabular-nums text-slate-400">
                        {s.toLocaleDateString("id-ID", { day: "numeric", month: "short" })} – {e.toLocaleDateString("id-ID", { day: "numeric", month: "short" })}
                      </span>
                      <span className="tabular-nums text-slate-400">· sisa {daysRemaining} hari</span>
                    </div>
                    {lr.delegate_name ? (
                      <div className="text-[11px] mt-1 flex items-center gap-1.5 text-emerald-700" data-testid={`on-leave-delegate-${lr.id}`}>
                        <UserCheck size={11} className="text-emerald-600" />
                        <span className="text-slate-500">Tugas didelegasikan ke:</span>
                        <span className="font-semibold text-[#0B1D3A]">{lr.delegate_name}</span>
                        {lr.delegate_division && <span className={`px-1 py-0 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[lr.delegate_division] || "bg-slate-100"}`}>{lr.delegate_division}</span>}
                        <span className={`px-1 py-0 text-[9px] uppercase tracking-wider border ${
                          lr.delegate_status === "approved"
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : lr.delegate_status === "rejected"
                            ? "border-rose-300 bg-rose-50 text-rose-700"
                            : "border-amber-300 bg-amber-50 text-amber-700"
                        }`}>
                          {lr.delegate_status === "approved" ? "Diterima" : lr.delegate_status === "rejected" ? "Ditolak" : "Menunggu"}
                        </span>
                      </div>
                    ) : (
                      <div className="text-[11px] mt-1 text-slate-400 italic" data-testid={`on-leave-no-delegate-${lr.id}`}>Tidak ada delegasi tugas untuk cuti ini</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      </Widget>

      {/* Filter summary — quick-glance totals for BOD/FAT when multi-project filter active */}
      <Widget id="filter_summary">
      {canSeeFinancials && selectedIds.length > 0 && (() => {
        const filteredProjects = projects.filter((p) => selectedIds.includes(p.id));
        const totalKontrak = filteredProjects.reduce((sum, p) => sum + (parseFloat(p.contract_value) || 0), 0);
        const totalIncome = filteredProjects.reduce((sum, p) => sum + (parseFloat(p.total_income) || 0), 0);
        const totalExpense = filteredProjects.reduce((sum, p) => sum + (parseFloat(p.total_expense) || 0), 0);

        // Trend comparison across cur/prev window depending on `trendPeriod`.
        // Window buckets:
        //   month:   current calendar month vs previous calendar month
        //   quarter: current calendar quarter vs previous calendar quarter
        //   ytd:     current year (up to today) vs same range of previous year
        const now = new Date();
        const todayMD = { m: now.getMonth(), d: now.getDate() };
        const curYear = now.getFullYear();
        const inRange = (dateStr, period, which) => {
          if (!dateStr) return false;
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return false;
          if (period === "month") {
            const target = which === "cur" ? new Date(curYear, now.getMonth(), 1) : new Date(curYear, now.getMonth() - 1, 1);
            return d.getFullYear() === target.getFullYear() && d.getMonth() === target.getMonth();
          }
          if (period === "quarter") {
            const curQ = Math.floor(now.getMonth() / 3);
            const q = which === "cur" ? curQ : (curQ === 0 ? 3 : curQ - 1);
            const y = which === "cur" ? curYear : (curQ === 0 ? curYear - 1 : curYear);
            const dq = Math.floor(d.getMonth() / 3);
            return d.getFullYear() === y && dq === q;
          }
          // ytd — from Jan 1 of that year up to today's month/day
          if (period === "custom") {
            const ds = dateStr.slice(0, 10);
            const range = which === "cur" ? { s: customRange.curStart, e: customRange.curEnd } : { s: customRange.prevStart, e: customRange.prevEnd };
            return ds >= range.s && ds <= range.e;
          }
          const y = which === "cur" ? curYear : curYear - 1;
          if (d.getFullYear() !== y) return false;
          if (d.getMonth() > todayMD.m) return false;
          if (d.getMonth() === todayMD.m && d.getDate() > todayMD.d) return false;
          return true;
        };
        const bucket = (entries) => {
          const acc = { cur: 0, prev: 0 };
          (entries || []).forEach((e) => {
            const dt = e.entry_date || e.date;
            if (inRange(dt, trendPeriod, "cur")) acc.cur += parseFloat(e.amount) || 0;
            else if (inRange(dt, trendPeriod, "prev")) acc.prev += parseFloat(e.amount) || 0;
          });
          return acc;
        };
        let inc = { cur: 0, prev: 0 };
        let exp = { cur: 0, prev: 0 };
        filteredProjects.forEach((p) => {
          const bi = bucket(p.income_entries);
          const be = bucket((p.expense_entries || []).filter((e) => e.excluded_from_totals !== true));
          inc.cur += bi.cur; inc.prev += bi.prev;
          exp.cur += be.cur; exp.prev += be.prev;
        });

        const periodLabel = trendPeriod === "month" ? "bulan berjalan vs bulan lalu"
          : trendPeriod === "quarter" ? "kuartal berjalan vs kuartal lalu"
          : trendPeriod === "ytd" ? "YTD tahun ini vs tahun lalu (periode sama)"
          : `${customRange.curStart} → ${customRange.curEnd} vs ${customRange.prevStart} → ${customRange.prevEnd}`;

        const Trend = ({ b, dir }) => {
          // dir === "up_good" means increase is good (income); "up_bad" means increase is bad (expense)
          if (b.cur === 0 && b.prev === 0) return null;
          const delta = b.cur - b.prev;
          const pct = b.prev > 0 ? Math.round((delta / b.prev) * 100) : (b.cur > 0 ? 100 : 0);
          const up = delta > 0;
          const flat = delta === 0;
          const positive = flat ? false : (dir === "up_good" ? up : !up);
          const color = flat ? "text-white/50" : positive ? "text-emerald-300" : "text-rose-300";
          const Arrow = flat ? AlertOctagon : up ? TrendingUp : TrendingDown;
          const sign = up ? "+" : "";
          return (
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold tabular-nums ${color}`} title={`${periodLabel} · nilai sebelumnya: Rp ${b.prev.toLocaleString("id-ID")}`}>
              <Arrow size={10} />
              {flat ? "flat" : `${sign}${pct}%`}
            </span>
          );
        };

        return (
          <div className="bg-gradient-to-r from-[#0B1D3A] to-[#1e3a8a] text-white px-4 py-3 border-l-4 border-[#FF5A1F]" data-testid="filter-summary">
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <div className="inline-flex items-center gap-1.5">
                <span className="overline text-white/60">Melihat</span>
                <span className="font-display text-lg font-bold tabular-nums" data-testid="fs-count">{filteredProjects.length}</span>
                <span className="text-white/80">project</span>
              </div>
              <div className="h-6 w-px bg-white/20" />
              <div className="inline-flex items-center gap-1.5">
                <span className="overline text-white/60">Total Kontrak</span>
                <span className="font-mono font-bold tabular-nums text-[#FF9E4A]" data-testid="fs-contract">{formatIDR(totalKontrak)}</span>
              </div>
              <div className="h-6 w-px bg-white/20" />
              <div className="inline-flex items-center gap-1.5">
                <span className="overline text-white/60">Total Pemasukan</span>
                <span className="font-mono font-bold tabular-nums text-emerald-300" data-testid="fs-income">{formatIDR(totalIncome)}</span>
                <Trend b={inc} dir="up_good" />
              </div>
              <div className="h-6 w-px bg-white/20" />
              <div className="inline-flex items-center gap-1.5">
                <span className="overline text-white/60">Total Pengeluaran</span>
                <span className="font-mono font-bold tabular-nums text-rose-300" data-testid="fs-expense">{formatIDR(totalExpense)}</span>
                <Trend b={exp} dir="up_bad" />
              </div>
              <span className="text-[10px] text-white/40 italic ml-auto">Trend: {periodLabel}</span>
              <div className="inline-flex border border-white/30 ml-2">
                {["month", "quarter", "ytd", "custom"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`fs-trend-${k}`}
                    onClick={() => setTrendPeriod(k)}
                    className={`px-2 py-0.5 text-[9px] uppercase tracking-wider font-semibold ${trendPeriod === k ? "bg-[#FF5A1F] text-white" : "text-white/70 hover:bg-white/10"}`}
                  >{k === "month" ? "MoM" : k === "quarter" ? "QoQ" : k === "ytd" ? "YTD" : "Custom"}</button>
                ))}
              </div>
            </div>
            {trendPeriod === "custom" && (
              <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap items-center gap-2 text-[10px]" data-testid="custom-range-picker">
                <span className="overline text-white/60">Periode Sekarang</span>
                <input type="date" data-testid="range-cur-start" value={customRange.curStart} onChange={(e) => setCustomRange((c) => ({ ...c, curStart: e.target.value }))} className="bg-white/10 border border-white/20 px-1 py-0.5 text-white text-[10px] tabular-nums" />
                <span className="text-white/60">→</span>
                <input type="date" data-testid="range-cur-end" value={customRange.curEnd} onChange={(e) => setCustomRange((c) => ({ ...c, curEnd: e.target.value }))} className="bg-white/10 border border-white/20 px-1 py-0.5 text-white text-[10px] tabular-nums" />
                <span className="ml-4 overline text-white/60">Vs Periode</span>
                <input type="date" data-testid="range-prev-start" value={customRange.prevStart} onChange={(e) => setCustomRange((c) => ({ ...c, prevStart: e.target.value }))} className="bg-white/10 border border-white/20 px-1 py-0.5 text-white text-[10px] tabular-nums" />
                <span className="text-white/60">→</span>
                <input type="date" data-testid="range-prev-end" value={customRange.prevEnd} onChange={(e) => setCustomRange((c) => ({ ...c, prevEnd: e.target.value }))} className="bg-white/10 border border-white/20 px-1 py-0.5 text-white text-[10px] tabular-nums" />
              </div>
            )}
          </div>
        );
      })()}
      </Widget>

      <Widget id="stat_cards">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
        <StatCard label="Total Tugas (Aktif + Selesai)" value={totals.total} icon={ListChecks} accent="text-[#0B1D3A]"
          loading={!tasksLoaded} error={tasksErr}
          hint="Sumber: GET /api/tasks. Scope: tugas yang terlihat Anda sesuai permission backend. Tidak termasuk 'dibatalkan' bila Anda filter proyek; termasuk di total global." />
        <StatCard label="Selesai" value={totals.completed} icon={CheckCircle2} accent="text-emerald-600"
          loading={!tasksLoaded} error={tasksErr}
          hint="status = 'selesai'. Sumber sama dgn Total Tugas." />
        <StatCard label="Aktif (Menunggu/Dikerjakan)" value={totals.pending} icon={Clock} accent="text-blue-600"
          loading={!tasksLoaded} error={tasksErr}
          hint="status aktif: tidak 'selesai' dan tidak 'dibatalkan'. Sumber sama dgn Total Tugas." />
        <StatCard label="Terlambat" value={totals.overdue} icon={AlertTriangle} accent="text-rose-600"
          loading={!tasksLoaded}
          hint="Sumber: GET /api/performance (summary.total_overdue) — dihitung backend berdasarkan deadline terlewat & status aktif." />
      </div>
      </Widget>

      {/* 2026-10-13 (R164): widget `task_lists` di-rework.
          Sebelum: section "Tasks For Me" (= forMe) + "Awaiting Your Verification" (= awaitingMyVerification)
                   100% DUPLIKAT dgn widget `pending_actions` di atas (variabel SAMA).
          Sekarang: section 1 = "Semua Tugas Saya Aktif" (forMe panjang dgn kolom lebih)
                    section 2 = "Tugas yang Saya Buat" (byMeActive — scope BEDA: yg saya buat utk orang lain)
          ID widget tidak diubah → layout tersimpan tetap kompatibel. */}
      <Widget id="task_lists">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="bg-white border border-slate-200" data-testid="section-my-active-tasks">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-lg tracking-tight">Tugas Saya (Aktif)</h2>
              <span className="px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-blue-100 text-blue-700 border border-blue-200" data-testid="my-active-count">{forMe.length}</span>
            </div>
            <button data-testid="link-my-tasks" onClick={() => nav("/tasks")} className="text-xs uppercase tracking-wider text-[#FF5A1F] font-semibold flex items-center gap-1">Lihat semua <ArrowRight size={12} /></button>
          </header>
          <div className="px-4 py-1.5 text-[10px] text-slate-500 italic border-b border-slate-100">Scope: tugas di-assign ke Anda · status aktif (bukan selesai/dibatalkan)</div>
          <ul>
            {!tasksLoaded && <li className="p-4 text-sm text-slate-400 italic">Memuat tugas…</li>}
            {tasksLoaded && tasksErr && <li className="p-4 text-sm text-rose-500 italic">Gagal memuat tugas. Coba Refresh di header.</li>}
            {tasksLoaded && !tasksErr && forMe.length === 0 && <li className="p-4 text-sm text-slate-400">Tidak ada tugas aktif untuk Anda saat ini.</li>}
            {forMe.slice(0, 6).map((t) => (
              <li key={t.id} className="px-4 py-3 border-b border-slate-100 flex items-center gap-3" data-testid={`my-active-row-${t.id}`}>
                <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${DIVISION_COLOR[t.creator_division]}`}>{t.creator_division}</span>
                <span className="text-sm text-[#0B1D3A] font-medium flex-1 truncate">{t.title}</span>
                <span className={`px-1.5 py-0.5 text-[9px] border ${STATUS_COLOR[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                <span className="text-xs tabular-nums text-slate-500 hidden sm:inline">{fmtDate(t.deadline)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white border border-slate-200" data-testid="section-created-by-me">
          <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <h2 className="font-display text-lg tracking-tight">Tugas yang Saya Buat</h2>
              <span className="px-1.5 py-0.5 text-[10px] font-bold tabular-nums bg-slate-100 text-slate-700 border border-slate-200" data-testid="by-me-count">{byMeActive.length}</span>
            </div>
            <span className="text-xs tabular-nums text-slate-500">{awaitingMyVerification.length} perlu verifikasi</span>
          </header>
          <div className="px-4 py-1.5 text-[10px] text-slate-500 italic border-b border-slate-100">Scope: tugas yang Anda delegasikan ke orang lain · masih aktif</div>
          <ul>
            {!tasksLoaded && <li className="p-4 text-sm text-slate-400 italic">Memuat tugas…</li>}
            {tasksLoaded && tasksErr && <li className="p-4 text-sm text-rose-500 italic">Gagal memuat tugas.</li>}
            {tasksLoaded && !tasksErr && byMeActive.length === 0 && <li className="p-4 text-sm text-slate-400">Belum ada tugas yang Anda delegasikan.</li>}
            {byMeActive.slice(0, 6).map((t) => (
              <li key={t.id} className="px-4 py-3 border-b border-slate-100 flex items-center gap-3" data-testid={`by-me-row-${t.id}`}>
                <span className={`px-1.5 py-0.5 text-[9px] font-semibold ${PRIORITY_COLOR[t.priority]}`}>{PRIORITY_LABEL[t.priority]}</span>
                <span className="text-sm text-[#0B1D3A] font-medium flex-1 truncate">{t.title}</span>
                <span className={`px-1.5 py-0.5 text-[9px] border ${STATUS_COLOR[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                <span className="text-xs text-slate-500 truncate max-w-[140px]">→ {t.assignee_name}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>
      </Widget>

      <Widget id="financial_by_year">
      {canSeeFinancials && projects.some((p) => p.project_type !== "internal") && (() => {
        // Aggregate all non-internal projects by year
        const projectStart = (p) => {
          if (p.project_type === "variation_order") return p.vo_start_date;
          if (p.project_type === "working_order") return p.wo_start_date || p.start_date;
          return p.start_date;
        };
        const yearMap = {}; // year -> { contract, income, expense, projects: Set, incomeEntries, expenseEntries }
        const bucket = (y) => {
          if (!yearMap[y]) yearMap[y] = { contract: 0, income: 0, expense: 0, projects: new Set(), incomeCount: 0, expenseCount: 0 };
          return yearMap[y];
        };
        projects.filter((p) => p.project_type !== "internal").forEach((p) => {
          const s = projectStart(p);
          if (s) {
            const y = String(s).slice(0, 4);
            const b = bucket(y);
            b.contract += Number(p.contract_value || 0);
            b.projects.add(p.id);
          }
          (p.income_entries || []).forEach((e) => {
            const y = String(e.date || "").slice(0, 4);
            if (y) { const b = bucket(y); b.income += Number(e.amount_received || e.amount || 0); b.incomeCount += 1; }
          });
          (p.expense_entries || []).filter((e) => e.excluded_from_totals !== true).forEach((e) => {
            const y = String(e.date || "").slice(0, 4);
            if (y) { const b = bucket(y); b.expense += Number(e.amount || 0); b.expenseCount += 1; }
          });
        });
        const years = Object.keys(yearMap).sort((a, b) => b.localeCompare(a));
        if (years.length === 0) return null;
        const grand = years.reduce((acc, y) => {
          const b = yearMap[y];
          acc.contract += b.contract; acc.income += b.income; acc.expense += b.expense; acc.projects += b.projects.size;
          return acc;
        }, { contract: 0, income: 0, expense: 0, projects: 0 });
        return (
          <section className="bg-white border border-slate-200" data-testid="section-financial-by-year">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="toggle-fin-year"
                  onClick={toggleYear}
                  aria-label={collapseYear ? "Buka section" : "Tutup section"}
                  className="text-slate-500 hover:text-[#FF5A1F] transition-colors"
                >
                  {collapseYear ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                </button>
                <h2 className="font-display text-lg tracking-tight">Project Financial Overview · By Year</h2>
              </div>
              <button data-testid="link-projects" onClick={() => nav("/projects")} className="text-xs uppercase tracking-wider text-[#FF5A1F] font-semibold flex items-center gap-1">Manage <ArrowRight size={12} /></button>
            </header>
            {!collapseYear && (
            <div className="p-4 space-y-3">
              {/* Grand total row */}
              <div className="bg-[#0B1D3A] text-white p-3 grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                  <div className="overline text-[10px] opacity-70">Semua Tahun</div>
                  <div className="font-display text-2xl tabular-nums text-[#FF5A1F]">TOTAL</div>
                </div>
                <div>
                  <div className="overline text-[10px] opacity-70">Projects</div>
                  <div className="font-display text-xl tabular-nums" data-testid="fin-total-projects">{grand.projects}</div>
                </div>
                <div>
                  <div className="overline text-[10px] opacity-70">Contract/PO</div>
                  <div className="font-display text-xl tabular-nums" data-testid="fin-total-contract">{formatIDR(grand.contract)}</div>
                </div>
                <div>
                  <div className="overline text-[10px] opacity-70">Pemasukan</div>
                  <div className="font-display text-xl tabular-nums text-emerald-300" data-testid="fin-total-income">{formatIDR(grand.income)}</div>
                </div>
                <div>
                  <div className="overline text-[10px] opacity-70">Pengeluaran</div>
                  <div className="font-display text-xl tabular-nums text-rose-300" data-testid="fin-total-expense">{formatIDR(grand.expense)}</div>
                </div>
              </div>

              {/* Per-year table */}
              <table className="w-full text-xs border border-slate-200" data-testid="fin-year-table">
                <thead className="bg-slate-50 uppercase tracking-wider text-[10px] text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Tahun</th>
                    <th className="text-right px-3 py-2">Jumlah Project</th>
                    <th className="text-right px-3 py-2">Nilai Kontrak/PO</th>
                    <th className="text-right px-3 py-2">Total Pemasukan</th>
                    <th className="text-right px-3 py-2">Total Pengeluaran</th>
                    <th className="text-right px-3 py-2">Net (Inc - Exp)</th>
                  </tr>
                </thead>
                <tbody>
                  {years.map((y) => {
                    const b = yearMap[y];
                    const net = b.income - b.expense;
                    return (
                      <tr key={y} className="border-t border-slate-100" data-testid={`fin-row-${y}`}>
                        <td className="px-3 py-2 font-semibold text-[#0B1D3A] tabular-nums">{y}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{b.projects.size}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#0B1D3A]">{formatIDR(b.contract)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">
                          {formatIDR(b.income)}
                          {b.incomeCount > 0 && <span className="text-[9px] text-slate-400 ml-1">({b.incomeCount}x)</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700">
                          {formatIDR(b.expense)}
                          {b.expenseCount > 0 && <span className="text-[9px] text-slate-400 ml-1">({b.expenseCount}x)</span>}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {net >= 0 ? <TrendingUp size={10} className="inline mr-0.5" /> : <TrendingDown size={10} className="inline mr-0.5" />}
                          {formatIDR(net)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="text-[10px] text-slate-500">
                * Nilai Kontrak dikelompokkan berdasarkan tanggal mulai project (start_date / vo_start_date / wo_start_date). Pemasukan &amp; Pengeluaran dikelompokkan berdasarkan tanggal entri di ledger.
              </div>
            </div>
            )}
          </section>
        );
      })()}
      </Widget>

      <Widget id="financial_by_month">
      {canSeeFinancials && projects.some((p) => p.project_type !== "internal") && (() => {
        // Aggregate all non-internal projects by year-month (last 24 months only)
        const monthMap = {}; // "YYYY-MM" -> { contract, income, expense, projects: Set, ... }
        const bucket = (ym) => {
          if (!monthMap[ym]) monthMap[ym] = { contract: 0, income: 0, expense: 0, projects: new Set(), incomeCount: 0, expenseCount: 0 };
          return monthMap[ym];
        };
        const projectStart = (p) => {
          if (p.project_type === "variation_order") return p.vo_start_date;
          if (p.project_type === "working_order") return p.wo_start_date || p.start_date;
          return p.start_date;
        };
        projects.filter((p) => p.project_type !== "internal").forEach((p) => {
          const s = projectStart(p);
          if (s) {
            const ym = String(s).slice(0, 7);
            const b = bucket(ym);
            b.contract += Number(p.contract_value || 0);
            b.projects.add(p.id);
          }
          (p.income_entries || []).forEach((e) => {
            const ym = String(e.date || "").slice(0, 7);
            if (ym) { const b = bucket(ym); b.income += Number(e.amount_received || e.amount || 0); b.incomeCount += 1; }
          });
          (p.expense_entries || []).filter((e) => e.excluded_from_totals !== true).forEach((e) => {
            const ym = String(e.date || "").slice(0, 7);
            if (ym) { const b = bucket(ym); b.expense += Number(e.amount || 0); b.expenseCount += 1; }
          });
        });
        // Sort desc, keep last 24 months
        const months = Object.keys(monthMap).sort((a, b) => b.localeCompare(a)).slice(0, 24);
        if (months.length === 0) return null;
        const monthLabel = (ym) => {
          const [y, m] = ym.split("-");
          const names = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
          return `${names[parseInt(m,10)-1]} ${y}`;
        };
        return (
          <section className="bg-white border border-slate-200" data-testid="section-financial-by-month">
            <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="toggle-fin-month"
                  onClick={toggleMonth}
                  aria-label={collapseMonth ? "Buka section" : "Tutup section"}
                  className="text-slate-500 hover:text-[#FF5A1F] transition-colors"
                >
                  {collapseMonth ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                </button>
                <h2 className="font-display text-lg tracking-tight">Project Financial Overview · By Month</h2>
              </div>
              <span className="text-[10px] text-slate-500 tabular-nums">{months.length} bulan terakhir</span>
            </header>
            {!collapseMonth && (
            <div className="p-4 space-y-3">
              {(() => {
                // Split bar chart — 2 chart terpisah (Pemasukan vs Pengeluaran) sesuai
                // request user 2026-02-11. Skala independen tiap chart supaya trend
                // kelihatan jelas walau nilai income >> expense (atau sebaliknya).
                const chartMonths = [...months].reverse().slice(-12);
                if (chartMonths.length === 0) return null;
                const maxInc = Math.max(1, ...chartMonths.map((ym) => monthMap[ym].income));
                const maxExp = Math.max(1, ...chartMonths.map((ym) => monthMap[ym].expense));
                const W = 520;
                const H = 160;
                const barW = Math.max(12, (W - 40) / chartMonths.length - 6);
                const renderChart = (title, color, valueOf, maxVal, testid) => {
                  const scale = (v) => (v / maxVal) * (H - 30);
                  return (
                    <div className="border border-slate-200 bg-slate-50 p-3 overflow-x-auto" data-testid={testid}>
                      <div className="flex items-center justify-between mb-2 text-[10px] text-slate-500 uppercase tracking-wider">
                        <span className="font-semibold" style={{ color }}>{title}</span>
                        <span className="tabular-nums text-slate-400">max: {formatIDR(maxVal)}</span>
                      </div>
                      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: W, maxWidth: 720 }} preserveAspectRatio="xMinYMid meet">
                        <line x1="30" x2={W - 5} y1={H - 25} y2={H - 25} stroke="#cbd5e1" strokeWidth="1" />
                        {chartMonths.map((ym, i) => {
                          const b = monthMap[ym];
                          const val = valueOf(b);
                          const xBase = 35 + i * (barW + 6);
                          const h = scale(val);
                          const yTop = H - 25 - h;
                          return (
                            <g key={ym}>
                              <rect x={xBase} y={yTop} width={barW} height={h} fill={color}>
                                <title>{`${monthLabel(ym)}: ${formatIDR(val)}`}</title>
                              </rect>
                              {val > 0 && h > 22 && (
                                <text x={xBase + barW / 2} y={yTop + 12} fontSize="8" textAnchor="middle" fill="white" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {val >= 1000000 ? `${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `${Math.round(val / 1000)}k` : Math.round(val)}
                                </text>
                              )}
                              <text x={xBase + barW / 2} y={H - 8} fontSize="8" textAnchor="middle" fill="#64748b" style={{ fontFamily: 'monospace' }}>
                                {monthLabel(ym).replace(" 20", " '")}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  );
                };
                return (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="fin-month-chart">
                    {renderChart("Pemasukan · 12 Bulan Terakhir", "#10b981", (b) => b.income, maxInc, "fin-month-chart-income")}
                    {renderChart("Pengeluaran · 12 Bulan Terakhir", "#f43f5e", (b) => b.expense, maxExp, "fin-month-chart-expense")}
                  </div>
                );
              })()}
              <table className="w-full text-xs border border-slate-200" data-testid="fin-month-table">
                <thead className="bg-slate-50 uppercase tracking-wider text-[10px] text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Bulan</th>
                    <th className="text-right px-3 py-2">Project</th>
                    <th className="text-right px-3 py-2">Nilai Kontrak/PO</th>
                    <th className="text-right px-3 py-2">Pemasukan</th>
                    <th className="text-right px-3 py-2">Pengeluaran</th>
                    <th className="text-right px-3 py-2">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((ym) => {
                    const b = monthMap[ym];
                    const net = b.income - b.expense;
                    return (
                      <tr key={ym} className="border-t border-slate-100" data-testid={`fin-month-row-${ym}`}>
                        <td className="px-3 py-2 font-semibold text-[#0B1D3A] tabular-nums whitespace-nowrap">{monthLabel(ym)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{b.projects.size || "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#0B1D3A]">{b.contract ? formatIDR(b.contract) : "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">
                          {b.income ? formatIDR(b.income) : "—"}
                          {b.incomeCount > 0 && <span className="text-[9px] text-slate-400 ml-1">({b.incomeCount}x)</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-rose-700">
                          {b.expense ? formatIDR(b.expense) : "—"}
                          {b.expenseCount > 0 && <span className="text-[9px] text-slate-400 ml-1">({b.expenseCount}x)</span>}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {net !== 0 && (net >= 0 ? <TrendingUp size={10} className="inline mr-0.5" /> : <TrendingDown size={10} className="inline mr-0.5" />)}
                          {net === 0 ? "—" : formatIDR(net)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[10px] text-slate-500">
                * Dikelompokkan berdasarkan tanggal (start_date / vo_start_date / wo_start_date atau tanggal entri ledger). Tampilan {months.length} bulan terakhir.
              </div>
            </div>
            )}
          </section>
        );
      })()}

      {perf && (
        <section className="bg-white border border-slate-200">
          <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-display text-lg tracking-tight">Performance Summary</h2>
            <button data-testid="link-performance" onClick={() => nav("/performance")} className="text-xs uppercase tracking-wider text-[#FF5A1F] font-semibold">View details →</button>
          </header>
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-x divide-slate-200">
            <div className="p-5"><p className="overline text-slate-500">Avg Response</p><p className="font-display text-2xl tabular-nums mt-1">{fmtDuration(perf.summary.avg_response_sec)}</p></div>
            <div className="p-5"><p className="overline text-slate-500">Fastest Completion</p><p className="font-display text-2xl tabular-nums mt-1 text-emerald-700">{fmtDuration(perf.summary.fastest_completion_sec)}</p></div>
            <div className="p-5"><p className="overline text-slate-500">Slowest Completion</p><p className="font-display text-2xl tabular-nums mt-1 text-rose-700">{fmtDuration(perf.summary.slowest_completion_sec)}</p></div>
          </div>
        </section>
      )}
      </Widget>
    </div>
  );
}
